import { useEffect, useMemo, useState } from 'react';
import QRCodeStyling from '@plasticprintersinc/pp-qr-code';
import { generateDrawerCSS } from '../../../../lib/drawer/styles';
import { generateDrawerHTML } from '../../../../lib/drawer/template';
import styles from './FramePreview.module.css';

export default function FramePreview({
  scriptTag,
  previewConfig = {},
  selectedQrStyle = null,
  connectDefaultUrl = '',
  useDummyPreview = false,
  refreshKey = null
}) {
    const [qrPreviewUrl, setQrPreviewUrl] = useState('');
    const qrDataValue = useMemo(() => selectedQrStyle?.data || selectedQrStyle?.qrData || '', [selectedQrStyle]);

    useEffect(() => {
        if (!useDummyPreview || !selectedQrStyle || !qrDataValue) {
            setQrPreviewUrl('');
            return;
        }

        const generate = async () => {
            try {
                const qr = new QRCodeStyling({
                    ...selectedQrStyle,
                    data: qrDataValue,
                });
                const blob = await qr.getRawData('png');

                const dataUrl = await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result);
                    reader.onerror = reject;
                    reader.readAsDataURL(blob);
                });

                setQrPreviewUrl(typeof dataUrl === 'string' ? dataUrl : '');
            } catch (err) {
                console.error('Failed to build QR preview', err);
                setQrPreviewUrl('');
            }
        };

        generate();
    }, [useDummyPreview, selectedQrStyle, qrDataValue]);

    const normalizedConfig = {
        ...previewConfig,
        displayOn: useDummyPreview
            ? 'all'
            : (previewConfig?.displayOn === 'mobile-only' ? 'mobile-only' : (previewConfig?.displayOn || 'all'))
    };

    const injectedScript = useDummyPreview ? `
            <style>${generateDrawerCSS(normalizedConfig)}</style>
            ${generateDrawerHTML({
                title: normalizedConfig.title || 'Scan to continue on your phone',
                tabText: normalizedConfig.tabText || 'Connect Now',
                showLogo: normalizedConfig.showLogo !== false
            })}
              <script>
                  (function(){
                      try {
                  var connectDefaultUrl = ${JSON.stringify(connectDefaultUrl || '')};
                  if (connectDefaultUrl && window.location.href && window.location.href.startsWith('data:text/html')) {
                    try {
                      history.replaceState(null, '', connectDefaultUrl);
                    } catch (e) {}
                  }
                      } catch(e) {}
                  })();
              </script>
                <script>
                        (function(){
                                try {
                        var tab = document.getElementById('ppDrawerTab');
                        var drawer = document.getElementById('ppDrawer');
                        var closeBtn = document.getElementById('ppDrawerClose');
                        var animateTab = ${normalizedConfig.animateTab === true};
                        var animateType = '${normalizedConfig.animateType || 'wiggle'}';
                        var jiggleInterval = null;
                        var hasInteracted = false;
                        var peakTriggered = false;

                        function triggerJiggle() {
                          if (!animateTab || animateType !== 'wiggle') return;
                          if (!hasInteracted && tab && !drawer.classList.contains('open')) {
                            tab.classList.add('pp-jiggle');
                            setTimeout(() => {
                              if (tab) tab.classList.remove('pp-jiggle');
                            }, 600);
                          }
                        }

                        function triggerPeak() {
                          if (!animateTab || animateType !== 'peak') return;
                          if (peakTriggered) return;  // Peak only triggers once
                          if (!hasInteracted && tab && drawer && !drawer.classList.contains('open')) {
                            peakTriggered = true;  // Mark as triggered
                            tab.classList.add('pp-peak');
                            drawer.classList.add('pp-peak');
                            setTimeout(() => {
                              if (tab) tab.classList.remove('pp-peak');
                              if (drawer) drawer.classList.remove('pp-peak');
                            }, 1000);
                          }
                        }

                        function triggerAnimation() {
                          if (animateType === 'wiggle') {
                            triggerJiggle();
                          } else if (animateType === 'peak') {
                            triggerPeak();
                          }
                        }

                        function stopAnimation() {
                          if (!animateTab) return;
                          hasInteracted = true;
                          if (jiggleInterval) {
                            clearInterval(jiggleInterval);
                            jiggleInterval = null;
                          }
                          if (tab) {
                            tab.classList.remove('pp-jiggle');
                            tab.classList.remove('pp-peak');
                          }
                        }

                        function updateDrawerPadding(){
                            if (!tab || !drawer) return;
                            var rect = tab.getBoundingClientRect();
                            var gap = Math.ceil(rect.width + 8);
                            drawer.style.setProperty('--pp-tab-gap', gap + 'px');
                        }
                        updateDrawerPadding();
                        window.addEventListener('resize', updateDrawerPadding);
                                        if (tab && drawer) {
                            tab.addEventListener('click', function(){ 
                              drawer.classList.add('open');
                              stopAnimation();
                            });
                                        }
                                        if (closeBtn && drawer) {
                            closeBtn.addEventListener('click', function(){ drawer.classList.remove('open'); });
                                        }

                        // Start animation only when enabled
                        if (animateTab) {
                          setTimeout(() => {
                            triggerAnimation();
                            jiggleInterval = setInterval(triggerAnimation, 15000);
                          }, 2000);
                        }

                        // Insert a preview QR image (preview only)
                        var qrContainer = document.getElementById('ppQrContainer');
                        if (qrContainer) {
                            qrContainer.innerHTML = ${'`'}<img src="${qrPreviewUrl || 'https://storage.googleapis.com/liquidqr-9b192.appspot.com/pp_qr/testqr.png'}" alt="QR Code" onerror="this.style.display=\\'none\\'">${'`'};
                        }
                                } catch(e) {}
                        })();
                </script>
        ` : scriptTag;

    return (
        <iframe
            key={useDummyPreview ? JSON.stringify({ ...normalizedConfig, qrPreviewUrl }) : JSON.stringify({ scriptTag, refreshKey })}
            src={`data:text/html;charset=utf-8,${encodeURIComponent(`
                <!DOCTYPE html>
                <html lang="en">
                    <head>
                        <meta charset="UTF-8">
                        <meta name="viewport" content="width=device-width, initial-scale=1.0">
                        <meta http-equiv="Cache-Control" content="no-cache, no-store, must-revalidate">
                        <meta http-equiv="Pragma" content="no-cache">
                        <meta http-equiv="Expires" content="0">
                        <title>Preview</title>
                        <style>
                            body {
                                margin: 0;
                                padding: 20px;
                                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                                background: #fbfbfb;
                                height: 100%;
                                overflow: hidden;
                            }
                            .content {
                                max-width: 600px;
                                margin: 0 auto;
                                padding: 2.5rem 1rem;
                            }
                            h1 {
                                color: #1f2937;
                                margin-bottom: 20px;
                                font-size: 28px;
                                margin-top: 0;
                            }
                            p {
                                color: #6b7280;
                                line-height: 1.6;
                                margin-bottom: 16px;
                            }
                            .highlight {
                                background: #edf0f6;
                                padding: 20px;
                                border-radius: 8px;
                                border-left: 4px solid #607ca7;
                                margin: 20px 0;
                            }
                        </style>
                    </head>
                    <body>
                    <div class="content">
                        <h1>Sample Web Page</h1>
                        <p>This is exactly how your drawer will appear on a real website. The script is actually loaded and running here.</p>
                        
                        <div class="highlight">
                        <strong>Try it out:</strong> Click the tab on the right edge to see your customized drawer in action!
                        </div>
                        
                        <p>Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.</p>
                        
                        <p>Your drawer positioning, sizing, colors, and content are all exactly as they will appear when you add the script to your website.</p>
                    </div>
                    
                    ${injectedScript}
                    </body>
                </html>
            `)}`}
            className={styles.iframe}
            title='Live Preview'
            sandbox='allow-scripts allow-same-origin'
        />
    );
};